import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faSpinner, faExclamationTriangle, faTimes, faPlus } from '@fortawesome/free-solid-svg-icons';
import '@medprompt/shared/src/common/modals.css';
import { ConsultationStatusEnum } from '../../enums/consultationStatusEnum';
import { Tile, ContentErrorBlock } from '../../detail/tabs/Tile';
import { ConsultationContentType } from '../../detail/tabs/contentTypes';
import { ConsultationService, sharedApiClient } from '../../fetcher';
import { useNotifications, CustomDropdown } from '@medprompt/shared';
import { getNotesModeDisplay, notesModeOptions } from '../notesModeEnum';
import NotesTemplateEditorModal from '../NotesTemplateEditorModal';

const ADD_TEMPLATE_ID = '__add_template__';

const shouldShowContent = (field, status) => {
  return field !== undefined || status === ConsultationStatusEnum.READY.value;
};

const NotesTab = ({ 
  consultation, 
  isEditable, 
  isRegenerationEnabled, 
  handleRegenerationClick, 
  regenerateService,
  onConsultationUpdate 
}) => {
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [editableNotes, setEditableNotes] = useState(consultation?.notes || '');
  const [isSaving, setIsSaving] = useState(false);
  const originalNotes = useRef(consultation?.notes || '');
  const notesTextareaRef = useRef(null);
  const isEditingNotesRef = useRef(false);

  // Pending dropdown values (edited but not yet saved)
  const [pendingTemplateId, setPendingTemplateId] = useState(consultation?.templateId || '');
  const [pendingNotesMode, setPendingNotesMode] = useState(consultation?.notesMode || 'MEDIUM');
  const originalTemplateId = useRef(consultation?.templateId || '');
  const originalNotesMode = useRef(consultation?.notesMode || 'MEDIUM');
  const [isEditingSettings, setIsEditingSettings] = useState(false);

  // Template state
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [showEditorModal, setShowEditorModal] = useState(false);

  // Warning modal for regeneration settings change
  const [showRegenerateWarning, setShowRegenerateWarning] = useState(false);

  const [templateDropdownOpen, setTemplateDropdownOpen] = useState(false);
  const [detailDropdownOpen, setDetailDropdownOpen] = useState(false);
  const templateDropdownRef = useRef(null);
  const detailDropdownRef = useRef(null);

  const consultationManager = useMemo(() => new ConsultationService(sharedApiClient), []);
  const { addNotification } = useNotifications();

  const status = consultation?.status;
  const consultationId = consultation?.consultationId;

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    try {
      setLoadingTemplates(true);
      const fetchedTemplates = await consultationManager.fetchNotesTemplates();
      setTemplates(fetchedTemplates || []);
    } catch (err) {
      console.error('Error fetching notes templates:', err);
    } finally {
      setLoadingTemplates(false);
    }
  }, [consultationManager]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Build template options with "Add template" at the end
  const templateTypeOptions = useMemo(() => {
    const options = templates.map(t => ({
      value: t.templateId,
      display: t.title
    }));
    options.push({
      value: ADD_TEMPLATE_ID,
      display: 'Add template',
      icon: faPlus
    });
    return options;
  }, [templates]);

  useEffect(() => {
    isEditingNotesRef.current = isEditingNotes;
  }, [isEditingNotes]);

  // Sync notes from the server without clobbering in-progress edits
  useEffect(() => {
    originalNotes.current = consultation?.notes || '';
    if (!isEditingNotesRef.current) {
      setEditableNotes(consultation?.notes || '');
    }
  }, [consultation?.notes]);

  // Sync template/detail independently so a notes save does not discard pending settings
  useEffect(() => {
    originalTemplateId.current = consultation?.templateId || '';
    setPendingTemplateId(consultation?.templateId || '');
    
    originalNotesMode.current = consultation?.notesMode || 'MEDIUM';
    setPendingNotesMode(consultation?.notesMode || 'MEDIUM');
  }, [consultation?.templateId, consultation?.notesMode]);

  useEffect(() => {
    if (isEditingNotes && notesTextareaRef.current) {
      notesTextareaRef.current.focus();
    }
  }, [isEditingNotes]);

  const handleNotesClick = useCallback(() => {
    if (!isEditable) return;
    setEditableNotes(consultation?.notes || '');
    setIsEditingNotes(true);
  }, [isEditable, consultation?.notes]);

  const handleNotesBlur = useCallback(() => {
    if (!consultationId) {
      setIsEditingNotes(false);
      return;
    }

    setIsEditingNotes(false);

    const originalText = originalNotes.current || '';
    if (editableNotes === originalText) {
      return;
    }

    const updatedConsultation = consultation
      ? { ...consultation, notes: editableNotes }
      : consultation;

    if (onConsultationUpdate && updatedConsultation) {
      onConsultationUpdate(updatedConsultation);
    }

    originalNotes.current = editableNotes;

    consultationManager.updateConsultation(consultationId, { notes: editableNotes })
      .catch(error => {
        console.error('Error saving notes:', error);
        addNotification(`notes-save-error-${consultationId}`, 'Failed to save notes', { isError: true });
      });
  }, [consultationId, editableNotes, consultation, onConsultationUpdate, consultationManager, addNotification]);

  const handleNotesKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      setEditableNotes(originalNotes.current || '');
      setIsEditingNotes(false);
    }
  }, []);

  // Check if regeneration settings (template or detail level) have changed
  const willRegenerateNotes = useMemo(() => {
    const templateChanged = pendingTemplateId !== originalTemplateId.current;
    const notesModeChanged = pendingNotesMode !== originalNotesMode.current;
    return templateChanged || notesModeChanged;
  }, [pendingTemplateId, pendingNotesMode]);

  // Actual save logic (called directly or after confirmation)
  const performSave = useCallback(async () => {
    if (!consultationId) return;
    
    setIsSaving(true);
    setShowRegenerateWarning(false);
    
    try {
      const templateChanged = pendingTemplateId !== originalTemplateId.current;
      const notesModeChanged = pendingNotesMode !== originalNotesMode.current;

      const updatePayload = {};
      if (templateChanged) updatePayload.templateId = pendingTemplateId;
      if (notesModeChanged) updatePayload.notesMode = pendingNotesMode;

      if (!templateChanged && !notesModeChanged) {
        setIsEditingSettings(false);
        return;
      }
      
      await consultationManager.updateConsultation(consultationId, updatePayload);
      
      // Optimistic update
      if (onConsultationUpdate && consultation) {
        onConsultationUpdate({
          ...consultation,
          templateId: pendingTemplateId,
          notesMode: pendingNotesMode
        });
      }
      
      originalTemplateId.current = pendingTemplateId;
      originalNotesMode.current = pendingNotesMode;
      
      setIsEditingSettings(false);
      addNotification(`notes-saved-${consultationId}`, 'Notes settings saved successfully');
    } catch (error) {
      console.error('Error saving notes settings:', error);
      addNotification(`notes-save-error-${consultationId}`, 'Failed to save notes settings', { isError: true });
    } finally {
      setIsSaving(false);
    }
  }, [consultationId, pendingTemplateId, pendingNotesMode, consultationManager, onConsultationUpdate, consultation, addNotification]);

  // Handle save button click - show warning if regeneration settings changed
  const handleSave = useCallback(() => {
    if (willRegenerateNotes) {
      setShowRegenerateWarning(true);
    } else {
      performSave();
    }
  }, [willRegenerateNotes, performSave]);

  const handleCancelRegenerate = useCallback(() => {
    setShowRegenerateWarning(false);
  }, []);

  const handleDiscard = useCallback(() => {
    setPendingTemplateId(originalTemplateId.current);
    setPendingNotesMode(originalNotesMode.current);
    setIsEditingSettings(false);
  }, []);

  const handleTemplateChange = useCallback((option) => {
    setTemplateDropdownOpen(false);
    
    if (option.value === ADD_TEMPLATE_ID) {
      setShowEditorModal(true);
      return;
    }
    
    if (option.value === pendingTemplateId) return;
    
    setPendingTemplateId(option.value);
    if (!isEditingSettings) setIsEditingSettings(true);
  }, [pendingTemplateId, isEditingSettings]);

  const handleDetailLevelChange = useCallback((option) => {
    setDetailDropdownOpen(false);
    if (option.value === pendingNotesMode) return;
    
    setPendingNotesMode(option.value);
    if (!isEditingSettings) setIsEditingSettings(true);
  }, [pendingNotesMode, isEditingSettings]);

  const handleTemplateSave = useCallback(async (templateData) => {
    try {
      const createData = {
        templateId: null,
        title: templateData.title,
        content: templateData.content
      };
      await consultationManager.createNotesTemplate(createData);
      
      addNotification('notes-tab', 'Notes template created successfully', { isError: false });
      
      setShowEditorModal(false);
      
      const fetchedTemplates = await consultationManager.fetchNotesTemplates();
      setTemplates(fetchedTemplates || []);
      
      const newTemplate = fetchedTemplates?.find(t => t.title === templateData.title);
      if (newTemplate) {
        setPendingTemplateId(newTemplate.templateId);
        if (!isEditingSettings) setIsEditingSettings(true);
      }
    } catch (err) {
      console.error('Error creating notes template:', err);
      addNotification('notes-tab', 'Failed to create notes template', { isError: true });
      throw err;
    }
  }, [consultationManager, addNotification, isEditingSettings]);

  const handleCloseEditor = useCallback(() => {
    setShowEditorModal(false);
  }, []);

  const getSelectedTemplateDisplay = useCallback(() => {
    if (loadingTemplates) return 'Loading...';
    if (!pendingTemplateId) return 'Select template';
    const template = templates.find(t => t.templateId === pendingTemplateId);
    return template ? template.title : 'Select template';
  }, [pendingTemplateId, templates, loadingTemplates]);

  const displayedNotes = isEditingNotes ? editableNotes : (consultation?.notes || '');

  return (
    <div className="notes-tab">
      <Tile
        doc={consultation}
        entityId={consultationId}
        contentType={ConsultationContentType.NOTES_STATUS}
        title="Consultation Notes"
        condition={shouldShowContent(consultation?.notes, status)}
        isEnabled={isRegenerationEnabled ? isRegenerationEnabled() : false}
        onRegenerateClick={handleRegenerationClick}
        regenerateService={regenerateService}
        showContentOnError
        headerContent={
          <div className="notes-header-controls">
            {isEditable && isEditingSettings && (
              <div className="notes-actions">
                <button
                  className="header-button"
                  onClick={handleDiscard}
                  disabled={isSaving}
                >
                  Discard
                </button>
                <button
                  className="header-button header-button-primary"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSave} /> Save
                    </>
                  )}
                </button>
              </div>
            )}

            <div className="notes-dropdowns">
              <div className="notes-dropdown-group">
                <span className="dropdown-label">Template:</span>
                <CustomDropdown
                  selected={getSelectedTemplateDisplay()}
                  options={templateTypeOptions}
                  isOpen={templateDropdownOpen}
                  toggleDropdown={() => setTemplateDropdownOpen(!templateDropdownOpen)}
                  onSelect={handleTemplateChange}
                  dropdownRef={templateDropdownRef}
                  disabled={!isEditable || loadingTemplates}
                  width="160px"
                />
              </div>

              <div className="notes-dropdown-group">
                <span className="dropdown-label">Detail:</span>
                <CustomDropdown
                  selected={getNotesModeDisplay(pendingNotesMode)}
                  options={notesModeOptions}
                  isOpen={detailDropdownOpen}
                  toggleDropdown={() => setDetailDropdownOpen(!detailDropdownOpen)}
                  onSelect={handleDetailLevelChange}
                  dropdownRef={detailDropdownRef}
                  disabled={!isEditable}
                  width="100px"
                />
              </div>
            </div>
          </div>
        }
      >
        <ContentErrorBlock
          doc={consultation}
          entityId={consultationId}
          contentType={ConsultationContentType.SUMMARY_STATUS}
          isRetryEnabled={isRegenerationEnabled ? isRegenerationEnabled() : false}
          onRegenerateClick={handleRegenerationClick}
          regenerateService={regenerateService}
        />
        <div className="notes-content">
        {isEditingNotes && isEditable ? (
          <textarea
            ref={notesTextareaRef}
            className="notes-description-textarea"
            value={editableNotes}
            onChange={(e) => setEditableNotes(e.target.value)}
            onBlur={handleNotesBlur}
            onKeyDown={handleNotesKeyDown}
          />
        ) : (
          <div
            className={`notes-description-text ${isEditable ? 'notes-description-editable' : ''}`}
            onClick={handleNotesClick}
            title={isEditable ? 'Click to edit' : ''}
            onMouseMove={(e) => {
              const tooltip = e.currentTarget.querySelector('.edit-tooltip');
              if (tooltip) {
                tooltip.style.left = `${e.clientX + 10}px`;
                tooltip.style.top = `${e.clientY + 10}px`;
              }
            }}
          >
            {displayedNotes || <em style={{ color: '#999' }}>No notes</em>}
            {isEditable && <div className="edit-tooltip">Click to edit notes</div>}
          </div>
        )}
      </div>
      </Tile>

      {showRegenerateWarning && (
        <div className="modal-overlay">
          <div className="modal-container modal-animated" style={{ width: '500px' }}>
            <div className="modal-header">
              <div className="modal-title">
                <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#f0ad4e', marginRight: '10px' }} />
                <h2>Regenerate Notes?</h2>
              </div>
              <button className="close-button" onClick={handleCancelRegenerate}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className="modal-content">
              <p>
                You have changed the <strong>
                  {pendingTemplateId !== originalTemplateId.current && pendingNotesMode !== originalNotesMode.current
                    ? 'template and detail level'
                    : pendingTemplateId !== originalTemplateId.current
                      ? 'template'
                      : 'detail level'}
                </strong> settings.
              </p>
              <p style={{ marginTop: '10px' }}>
                Saving these changes will <strong>regenerate the consultation notes</strong>, 
                which will overwrite any manual edits you have made to the notes content.
              </p>
              <p style={{ marginTop: '10px', color: '#666' }}>
                Are you sure you want to continue?
              </p>
            </div>
            
            <div className="modal-actions">
              <button className="cancel-button" onClick={handleCancelRegenerate}>
                Cancel
              </button>
              <button 
                className="dismiss-button" 
                onClick={performSave}
                disabled={isSaving}
                style={{ backgroundColor: '#f0ad4e', borderColor: '#f0ad4e' }}
              >
                {isSaving ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  'Regenerate Notes'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditorModal && (
        <NotesTemplateEditorModal
          template={null}
          onSave={handleTemplateSave}
          onClose={handleCloseEditor}
        />
      )}
    </div>
  );
};

export default NotesTab;
